/**
 * Radar — World view : matrice continent × secteur + form de scan ciblé + liste filtrée.
 */
import { useMemo } from "react";
import { Wand2, Globe, Loader2, Layers } from "lucide-react";
import { GEO_SCALE_OPTIONS } from "@/lib/geoScale";
import { SECTOR_META, CONTINENT_META, KPI } from "./constants";
import { SignalItem } from "./SignalItem";

export function WorldView({ matrix, items, loading, scanning, form, setForm, filter, setFilter, runScan, ack, openImpact, active }) {
  const continents = matrix?.continents || ["europe", "afrique", "amerique_nord", "amerique_sud", "asie", "moyen_orient", "oceanie"];
  const sectors = matrix?.categories || Object.keys(SECTOR_META).filter((s) => s !== "defaut");

  const cellMap = useMemo(() => {
    const m = {};
    (matrix?.cells || []).forEach((c) => {
      const key = `${c.continent || "monde"}:${c.sector}`;
      if (!m[key]) m[key] = { count: 0, unread: 0 };
      m[key].count += c.count;
      m[key].unread += c.unread;
    });
    return m;
  }, [matrix]);

  const maxCount = useMemo(() => {
    let mx = 0;
    Object.values(cellMap).forEach((v) => { if (v.count > mx) mx = v.count; });
    return mx || 1;
  }, [cellMap]);

  const totalSignals = (matrix?.cells || []).reduce((a, c) => a + c.count, 0);
  const totalUnread = (matrix?.cells || []).reduce((a, c) => a + c.unread, 0);

  return (
    <div className="space-y-4">
      {/* Scan form */}
      <div className="noam-card p-4 space-y-3" data-testid="radar-world-scan-form">
        <div className="flex items-center gap-2">
          <Wand2 className="w-3.5 h-3.5 text-[#378ADD]" />
          <div className="noam-overline">Lancer un scan ciblé (hybrid AI)</div>
        </div>
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          Choisissez une <strong className="text-zinc-200">cellule géographie × secteur</strong>. Noam interroge Tavily en temps réel,
          stocke les signaux, classifie les asymétries touchées. Cliquez ensuite sur n'importe quel signal pour
          obtenir l'<strong className="text-[#378ADD]">impact sur votre organisation</strong>.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">Continent</label>
            <select
              value={form.continent}
              onChange={(e) => setForm({ ...form, continent: e.target.value })}
              className="noam-input w-full text-xs mt-1"
              data-testid="radar-world-continent"
            >
              <option value="">Mondial</option>
              {continents.map((c) => (
                <option key={c} value={c}>{CONTINENT_META[c]?.label || c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">Pays (libre)</label>
            <input
              type="text"
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              placeholder="ex: Côte d'Ivoire"
              className="noam-input w-full text-xs mt-1"
              data-testid="radar-world-country"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">Secteur</label>
            <select
              value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              className="noam-input w-full text-xs mt-1"
              data-testid="radar-world-sector"
            >
              {sectors.map((s) => (
                <option key={s} value={s}>{SECTOR_META[s]?.label || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500">Niveau</label>
            <select
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
              className="noam-input w-full text-xs mt-1"
              data-testid="radar-world-level"
            >
              {GEO_SCALE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={runScan}
              disabled={scanning || !active}
              className="btn-primary text-xs w-full disabled:opacity-50"
              data-testid="radar-world-scan-btn"
            >
              {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
              {scanning ? "Scan…" : "Scanner"}
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Signaux mondiaux" value={totalSignals} accent="#378ADD" />
        <KPI label="Non lus (mondial)" value={totalUnread} accent="#EF9F27" />
        <KPI label="Cellules scannées" value={(matrix?.cells || []).length} accent="#1D9E75" />
      </div>

      {/* Heatmap matrix */}
      <div className="noam-card p-4 overflow-x-auto" data-testid="radar-world-matrix">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-[#1D9E75]" />
            <div className="noam-overline">Matrice continent × secteur</div>
          </div>
          <div className="text-[10px] text-zinc-500">
            Cliquez une cellule pour filtrer la liste ci-dessous.
            {(filter.continent || filter.sector) && (
              <button
                type="button"
                onClick={() => setFilter({ continent: "", sector: "" })}
                className="ml-2 text-[#EF9F27] hover:underline"
                data-testid="radar-world-clear-filter"
              >
                Effacer le filtre
              </button>
            )}
          </div>
        </div>
        <table className="text-[10px] w-full min-w-[700px]">
          <thead>
            <tr>
              <th className="text-left p-1 text-zinc-500 font-normal w-32"></th>
              {sectors.map((s) => (
                <th key={s} className="text-center p-1 font-normal text-zinc-400" style={{ color: SECTOR_META[s]?.color }}>
                  {SECTOR_META[s]?.label || s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {continents.map((c) => (
              <tr key={c}>
                <td className="text-left p-1 text-zinc-300 font-semibold">
                  <span className="mr-1">{CONTINENT_META[c]?.flag || "🌐"}</span>
                  {CONTINENT_META[c]?.label || c}
                </td>
                {sectors.map((s) => {
                  const key = `${c}:${s}`;
                  const cell = cellMap[key] || { count: 0, unread: 0 };
                  const intensity = cell.count > 0 ? Math.min(1, cell.count / maxCount) : 0;
                  const bg = cell.count === 0
                    ? "rgba(255,255,255,0.02)"
                    : `rgba(55,138,221,${0.10 + intensity * 0.55})`;
                  const isFiltered = filter.continent === c && filter.sector === s;
                  return (
                    <td
                      key={s}
                      className="p-1 text-center cursor-pointer hover:ring-1 hover:ring-[#378ADD] transition-all relative"
                      style={{
                        backgroundColor: bg,
                        border: isFiltered ? "1px solid #EF9F27" : "1px solid rgba(255,255,255,0.04)",
                      }}
                      onClick={() => setFilter({ continent: c, sector: s })}
                      data-testid={`radar-world-cell-${c}-${s}`}
                      title={`${cell.count} signal(aux), ${cell.unread} non lu(s)`}
                    >
                      <div className="font-bold text-zinc-100">{cell.count || ""}</div>
                      {cell.unread > 0 && (
                        <div className="text-[8px] text-[#EF9F27] font-bold">{cell.unread}●</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {totalSignals === 0 && (
          <div className="text-center text-[11px] text-zinc-500 italic mt-4">
            Aucun scan mondial pour le moment. Utilisez le formulaire au-dessus pour lancer un scan ciblé.
          </div>
        )}
      </div>

      {/* Signal list */}
      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center" data-testid="radar-world-empty">
          <Globe className="w-8 h-8 text-[#1D9E75] mx-auto opacity-50" />
          <div className="text-sm text-zinc-300 mt-2">
            {filter.continent || filter.sector ? "Aucun signal dans cette cellule." : "Aucun signal mondial."}
          </div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="radar-world-feed">
          {items.map((s) => (
            <SignalItem key={s.id} s={s} ack={ack} openImpact={openImpact} showGeo />
          ))}
        </ul>
      )}
    </div>
  );
}
